// Huawei Health Kit Integration Service
// Documentation: https://developer.huawei.com/consumer/en/doc/HMSCore-References/rest-overview-0000001254420693

const HUAWEI_CLIENT_ID = import.meta.env.REACT_APP_HUAWEI_CLIENT_ID || process.env.REACT_APP_HUAWEI_CLIENT_ID;
const HUAWEI_CLIENT_SECRET = import.meta.env.REACT_APP_HUAWEI_CLIENT_SECRET || process.env.REACT_APP_HUAWEI_CLIENT_SECRET;
const HUAWEI_REDIRECT_URI = import.meta.env.REACT_APP_HUAWEI_REDIRECT_URI || process.env.REACT_APP_HUAWEI_REDIRECT_URI || window.location.origin + '/patient/devices/callback';

// Huawei OAuth endpoints
const HUAWEI_AUTH_URL = 'https://oauth-login.cloud.huawei.com/oauth2/v3/authorize';
const HUAWEI_TOKEN_URL = 'https://oauth-login.cloud.huawei.com/oauth2/v3/token';
const HUAWEI_HEALTH_API = 'https://health-api.cloud.huawei.com/healthkit/v1';

// Health data scopes
export const HEALTH_SCOPES = [
  'https://www.huawei.com/healthkit/step.read',
  'https://www.huawei.com/healthkit/heartrate.read',
  'https://www.huawei.com/healthkit/sleep.read',
  'https://www.huawei.com/healthkit/oxygen.read',
  'https://www.huawei.com/healthkit/activity.read',
  'https://www.huawei.com/healthkit/calories.read',
  'https://www.huawei.com/healthkit/stress.read',
];

// Check if Huawei credentials are configured
export const isHuaweiConfigured = () => {
  return !!(HUAWEI_CLIENT_ID && HUAWEI_CLIENT_SECRET);
};

// Generate OAuth authorization URL
export const getHuaweiAuthUrl = () => {
  if (!isHuaweiConfigured()) {
    console.warn('Huawei Health Kit credentials not configured');
    return null;
  }

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: HUAWEI_CLIENT_ID,
    redirect_uri: HUAWEI_REDIRECT_URI,
    scope: HEALTH_SCOPES.join(' '),
    access_type: 'offline',
    state: generateState(),
  });

  return `${HUAWEI_AUTH_URL}?${params.toString()}`;
};

// Generate random state for OAuth security
const generateState = () => {
  return Math.random().toString(36).substring(2, 15);
};

// Exchange authorization code for access token
export const exchangeCodeForToken = async (code) => {
  if (!isHuaweiConfigured()) {
    throw new Error('Huawei Health Kit not configured');
  }

  const response = await fetch(HUAWEI_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: HUAWEI_CLIENT_ID,
      client_secret: HUAWEI_CLIENT_SECRET,
      redirect_uri: HUAWEI_REDIRECT_URI,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to exchange code for token');
  }

  return response.json();
};

// Refresh access token
export const refreshAccessToken = async (refreshToken) => {
  const response = await fetch(HUAWEI_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: HUAWEI_CLIENT_ID,
      client_secret: HUAWEI_CLIENT_SECRET,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to refresh token');
  }

  return response.json();
};

// Fetch health data from Huawei Health Kit
export const fetchHealthData = async (accessToken, dataType, startTime, endTime) => {
  const response = await fetch(`${HUAWEI_HEALTH_API}/${dataType}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    // Add query params for time range if needed
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${dataType} data`);
  }

  return response.json();
};

// ==================== MOCK DATA ====================
// Used when Huawei credentials are not configured

// Generate realistic mock data for today
const generateTodayData = () => {
  const now = new Date();
  const hourOfDay = now.getHours();
  
  // Steps increase throughout the day
  const baseSteps = Math.floor(hourOfDay * 450 + Math.random() * 200);
  
  return {
    steps: Math.min(baseSteps, 12000),
    calories: Math.floor(baseSteps * 0.04 + 1800), // BMR + activity
    distance: (baseSteps * 0.0007).toFixed(2), // km
    heartRate: {
      current: Math.floor(65 + Math.random() * 20),
      min: Math.floor(55 + Math.random() * 10),
      max: Math.floor(100 + Math.random() * 30),
      resting: Math.floor(58 + Math.random() * 8),
    },
    spo2: Math.floor(96 + Math.random() * 3),
    stress: Math.floor(25 + Math.random() * 35),
    sleep: {
      duration: 7.2 + (Math.random() - 0.5),
      quality: Math.floor(75 + Math.random() * 20),
      deep: 1.5 + Math.random() * 0.5,
      light: 4 + Math.random() * 1,
      rem: 1.5 + Math.random() * 0.5,
      awake: 0.2 + Math.random() * 0.3,
    },
  };
};

// Generate weekly trend data
const generateWeeklyTrend = (metric) => {
  const data = [];
  const now = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    
    let value;
    switch (metric) {
      case 'steps':
        value = Math.floor(6000 + Math.random() * 6000);
        break;
      case 'heartRate':
        value = Math.floor(62 + Math.random() * 15);
        break;
      case 'spo2':
        value = Math.floor(95 + Math.random() * 4);
        break;
      case 'sleep':
        value = 6 + Math.random() * 2.5;
        break;
      case 'stress':
        value = Math.floor(20 + Math.random() * 40);
        break;
      case 'calories':
        value = Math.floor(1800 + Math.random() * 800);
        break;
      default:
        value = Math.random() * 100;
    }
    
    data.push({
      date: date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' }),
      value: metric === 'sleep' ? parseFloat(value.toFixed(1)) : Math.floor(value),
    });
  }
  
  return data;
};

// Generate heart rate throughout the day
const generateHeartRateTimeline = () => {
  const data = [];
  const now = new Date();
  const currentHour = now.getHours();
  
  for (let i = 0; i <= currentHour; i++) {
    // Simulate lower HR during sleep, higher during activity
    let baseHR;
    if (i >= 0 && i < 6) baseHR = 58; // Sleep
    else if (i >= 6 && i < 9) baseHR = 70; // Morning
    else if (i >= 9 && i < 12) baseHR = 75; // Work
    else if (i >= 12 && i < 14) baseHR = 80; // Lunch/activity
    else if (i >= 14 && i < 18) baseHR = 72; // Afternoon
    else baseHR = 68; // Evening
    
    data.push({
      time: `${i.toString().padStart(2, '0')}:00`,
      value: Math.floor(baseHR + (Math.random() - 0.5) * 15),
    });
  }
  
  return data;
};

// Mock connected device info
export const getMockDeviceInfo = () => ({
  connected: true,
  device: {
    name: 'Huawei Watch GT 4',
    model: 'OCB-B19',
    battery: Math.floor(60 + Math.random() * 35),
    lastSync: new Date().toISOString(),
  },
});

// Get all health data (mock or real)
export const getAllHealthData = async (accessToken = null) => {
  // If no token or not configured, return mock data
  if (!accessToken || !isHuaweiConfigured()) {
    console.log('Using mock health data (Huawei not configured)');
    return {
      isDemo: true,
      today: generateTodayData(),
      trends: {
        steps: generateWeeklyTrend('steps'),
        heartRate: generateWeeklyTrend('heartRate'),
        spo2: generateWeeklyTrend('spo2'),
        sleep: generateWeeklyTrend('sleep'),
        stress: generateWeeklyTrend('stress'),
        calories: generateWeeklyTrend('calories'),
      },
      heartRateTimeline: generateHeartRateTimeline(),
      device: getMockDeviceInfo(),
    };
  }

  // Real API calls would go here
  try {
    // Fetch real data from Huawei Health Kit
    // const steps = await fetchHealthData(accessToken, 'steps', ...);
    // const heartRate = await fetchHealthData(accessToken, 'heartrate', ...);
    // ... etc
    
    // For now, return mock data as placeholder
    return {
      isDemo: false,
      // Real data would be processed here
    };
  } catch (error) {
    console.error('Error fetching health data:', error);
    throw error;
  }
};

// Store/retrieve tokens from localStorage
export const storeTokens = (tokens) => {
  localStorage.setItem('huawei_tokens', JSON.stringify({
    ...tokens,
    stored_at: Date.now(),
  }));
};

export const getStoredTokens = () => {
  const stored = localStorage.getItem('huawei_tokens');
  if (!stored) return null;
  
  const tokens = JSON.parse(stored);
  
  // Check if token is expired (with 5 min buffer)
  const expiresAt = tokens.stored_at + (tokens.expires_in * 1000) - 300000;
  if (Date.now() > expiresAt) {
    return { ...tokens, expired: true };
  }
  
  return tokens;
};

export const clearTokens = () => {
  localStorage.removeItem('huawei_tokens');
};
